// ---------------------------------------------------------------------------
// Toast host. One surface for everything that floats: the room's own lines and
// the restart notice.
//
// `gameStore.toasts` is still the queue: the socket layer pushes a line onto it
// and forgets about it. This component drains the store as soon as it reads it
// and hands each line to `react-hot-toast`. Leaving messages in the store would
// replay every toast of the session on the next remount.
//
// AN EARLIER SESSION REJECTED THIS LIBRARY. Its two objections were real and
// this is how each is answered rather than ignored.
//
//   1. "Two queues for one set of messages, and a second source of truth for
//      what the player has already been told." The store is still the only
//      place a *pending* line lives, and the drain is unchanged. What the
//      library holds is what is currently on screen — presentation state, which
//      the old host held too, in `useState`. The part that would really have
//      been a second truth is a library list that outlived a kick: that is
//      closed by `dropQueuedToasts`, called from `gameStore.clear()`, which
//      takes exactly the room's own cards away and leaves the restart notice.
//   2. "What it was actually being asked for was the look." The look is not the
//      library's. `toast.custom` means it renders none of its own chrome: the
//      tone skins, the mark, the exit that is not a disappearance, the drain
//      bar, the whole-card tap target and the `data-toast`/`data-tone` hooks
//      are all still here. What the library supplies is the timing, the stack
//      offsets, the hover pause and the removal — the mechanics, not the paint.
//
// Two structural rules survive from the old host:
//
//   1. A leaving card must not resize the stack. It animates out on the
//      transform layer and is removed once it has gone, so the cards under it
//      do not jump up under a thumb that is reaching for one of them.
//   2. The whole card is the dismiss target. A toast can land over a tile
//      mid-round, and a small cross is not something to hunt for against a
//      thirty second clock.
//
// THE Z LADDER. The room stacks, bottom up: chat backdrop 20, chat drawer 30,
// DisconnectSheet 40, ServerPauseOverlay 50, the leave confirmation 55, and
// toasts on top at 60. The library's container defaults to 9999, which would
// put a "Susie joined" over a blocking overlay and leave the ladder saying
// nothing; it is set to 60 here so the order stays the one written down. The
// z-9999 the library puts on each visible card is inside that container, which
// is a stacking context of its own, so it cannot climb out.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import type { Toast } from 'react-hot-toast';
import { AlertIcon, CheckIcon, Icon } from '../primitives/Icon.js';
import type { IconData } from '../primitives/Icon.js';
import { normalizeToast, trackQueuedToast } from '../../lib/toast.js';
import type { ToastQueue, ToastTone } from '../../lib/toast.js';
import { useGameStore } from '../../store/gameStore.js';

/** Long enough to read a sentence, short enough not to sit over the board. */
const DISMISS_MS = 5_000;

/** How long the card takes to leave, and how long it is kept mounted to do it. */
const LEAVE_MS = 220;

/** Older ones drop off the top rather than pushing the board off the screen. */
const MAX_VISIBLE = 3;

/**
 * Skins, per tone.
 *
 * Each is an opaque fill rather than a wash over whatever is behind it. A toast
 * lands over the board, and a translucent card composites against nine blue
 * tiles into a mid-tone that neither ink nor ink-on-fill is solved for.
 */
const TONE: Record<ToastTone, { skin: string; mark: string; icon: IconData | null }> = {
  info: { skin: 'border-line bg-surface text-ink', mark: 'text-ink-muted', icon: null },
  good: { skin: 'border-good/55 bg-surface text-ink', mark: 'text-good', icon: CheckIcon },
  alert: { skin: 'border-alert/55 bg-surface text-ink', mark: 'text-alert', icon: AlertIcon },
};

/**
 * The drain bar's motion, shipped from here rather than from the stylesheet.
 *
 * An animation rather than a transition, for one reason: the library pauses a
 * card's timer while a pointer is over the stack, and `animation-play-state` is
 * the only way to make the bar tell the same story. A bar that emptied while
 * the card it belongs to was not going anywhere would be a decoration that
 * lies.
 *
 * Both rules are reached by the reduced-motion kill switch in index.css, which
 * clamps every animation on the page to 0.01ms: the bar then reads as a static
 * empty rail, exactly as it did when this was a transition.
 */
const DRAIN_CSS = `
@keyframes countdown-toast-drain { from { height: 100%; } to { height: 0%; } }
[data-rht-toaster]:hover [data-toast-drain] { animation-play-state: paused; }
`;

export function Toasts() {
  const queued = useGameStore((s) => s.toasts);
  // StrictMode runs an effect twice on mount with the same dependency value, and
  // the second run would show every message a second time. Keyed on the array
  // identity because the store replaces the array on every push.
  const drained = useRef<ToastQueue | null>(null);

  useEffect(() => {
    if (queued.length === 0 || drained.current === queued) return;
    drained.current = queued;

    const taken = queued.slice();
    // Slice by the count read rather than emptying the array: a push that landed
    // between the render and this effect must not be swallowed.
    useGameStore.setState((current) => ({ toasts: current.toasts.slice(taken.length) }));

    for (const entry of taken) {
      const { message, tone } = normalizeToast(entry);
      const id = toast.custom((t) => (
        <ToastCard t={t} tone={tone}>
          {message}
        </ToastCard>
      ));
      trackQueuedToast(id, MAX_VISIBLE);
    }
  }, [queued]);

  return (
    <>
      <style>{DRAIN_CSS}</style>
      <Toaster
        position="top-center"
        gutter={8}
        containerStyle={{
          zIndex: 60,
          top: 'calc(12px + env(safe-area-inset-top))',
          left: 16,
          right: 16,
          bottom: 16,
        }}
        toastOptions={{ duration: DISMISS_MS, removeDelay: LEAVE_MS }}
      />
    </>
  );
}

export interface ToastCardProps {
  t: Toast;
  tone: ToastTone;
  /** Off for a card that does not expire, which has nothing to count down. */
  drain?: boolean;
  onClick?: () => void;
  children: ReactNode;
}

/**
 * The card. Shared by the room's lines and the restart notice, so the two
 * cannot drift into looking like different kinds of thing.
 */
export function ToastCard({ t, tone, drain = true, onClick, children }: ToastCardProps) {
  const skin = TONE[tone];
  // Enter is still ours: the library flips `visible` for the exit but shows a
  // card at rest on its first frame, and a card that appears without moving
  // reads as a repaint rather than as an arrival.
  const settled = useSettled();
  const shown = settled && t.visible;

  return (
    <button
      type="button"
      data-toast=""
      data-tone={tone}
      onClick={onClick ?? (() => toast.dismiss(t.id))}
      className={`pointer-events-auto flex min-h-11 w-full max-w-[420px] items-center gap-3 overflow-hidden rounded-card border px-4 py-3 text-left text-sm/[1.4] shadow-lg ${skin.skin} transition duration-200 ease-out ${
        shown ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
      }`}
    >
      {skin.icon === null ? null : (
        <Icon icon={skin.icon} size={17} className={`mt-px self-start ${skin.mark}`} />
      )}
      {/* The live region is on the line rather than on the container: the
          container belongs to the library, and a card is announced when it
          arrives rather than the whole stack being re-read. */}
      <span {...t.ariaProps} className="min-w-0 flex-1">
        {children}
      </span>
      {drain ? <Timer tone={tone} /> : null}
    </button>
  );
}

/** True from the frame after mount, so a class change has something to animate. */
function useSettled(): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return settled;
}

/**
 * How long the card has left, as a bar that drains.
 *
 * It is the reason a toast no longer feels like it vanished at random: the card
 * says when it is going. One animation and no re-renders — a toast that
 * re-rendered sixty times a second while a round was live would be a real cost
 * for a decoration.
 */
function Timer({ tone }: { tone: ToastTone }) {
  return (
    <span
      aria-hidden="true"
      className="h-6 w-0.5 shrink-0 self-center overflow-hidden rounded-full bg-line"
    >
      <span
        data-toast-drain=""
        className={`block w-full rounded-full ${
          tone === 'alert' ? 'bg-alert' : tone === 'good' ? 'bg-good' : 'bg-ink-dim'
        }`}
        style={{ height: '100%', animation: `countdown-toast-drain ${DISMISS_MS}ms linear forwards` }}
      />
    </span>
  );
}
